import React, { Component } from 'react';
import getContract from '../../services/contract';
import getTokenContract from '../../services/tokenContract';
import { ethers } from 'ethers';
import levelPrices from '../../services/levelPrice';
import getWallet from '../../services/wallet';
import Slot from './tree/slot';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// USDT contract address (BSC)
const USDT_ADDRESS = '0x55d398326f99059fF775485246999027B3197955';

// Main contract address whose USDT balance we want to display
const MAIN_CONTRACT_ADDRESS = '0x066fb0358AfFfA1E63dcEE09EaDD3325fb7F1d37';

// Minimal ERC20 ABI
const USDT_ABI = [
    'function balanceOf(address owner) view returns (uint256)',
    'function decimals() view returns (uint8)',
    'function symbol() view returns (string)'
];

class MainContractFunds extends Component {

    state = {
        amount: 0,
        isLoading: false,
        usdtBalance: '0',
        usdtSymbol: 'USDT',
        balanceLoading: true
    };

    componentDidMount() {
        this.loadUsdtBalance();
        this.balanceInterval = setInterval(this.loadUsdtBalance, 30000);
    }

    componentWillUnmount() {
        if (this.balanceInterval) clearInterval(this.balanceInterval);
    }

    loadUsdtBalance = async () => {
        try {
            const wallet = await getWallet();

            const provider =
                wallet && !wallet.isReadOnly
                    ? wallet.provider || wallet
                    : ethers.getDefaultProvider('https://bsc-dataseed.binance.org/');

            const usdtContract = new ethers.Contract(USDT_ADDRESS, USDT_ABI, provider);

            const [balance, decimals, symbol] = await Promise.all([
                usdtContract.balanceOf(MAIN_CONTRACT_ADDRESS),
                usdtContract.decimals(),
                usdtContract.symbol()
            ]);

            const formatted = ethers.utils.formatUnits(balance, decimals);

            this.setState({
                usdtBalance: formatted,
                usdtSymbol: symbol,
                balanceLoading: false
            });
        } catch (error) {
            console.error('Error loading USDT balance:', error);
            this.setState({ balanceLoading: false });
        }
    };

    handleUpdate = async () => {
        if (this.state.isLoading) return;

        try {
            const wallet = await getWallet();
            if (wallet.isReadOnly) {
                alert('Please connect your wallet');
                return;
            }

            const contract = await getContract();
            const transaction = await contract.getFunds();

            this.setState({ isLoading: true });

            const receipt = await toast.promise(transaction.wait(), {
                pending: 'Processing transaction...',
                success: 'Funds sent to wallet successfully! 🎉',
                error: 'Transaction failed'
            });

            console.log('Transaction receipt:', receipt);
            this.setState({ isLoading: false });
            this.loadUsdtBalance();
        } catch (error) {
            this.setState({ isLoading: false });

            if (error.code === 4001) {
                alert('Transaction rejected by user');
            } else if (error.reason) {
                alert(error.reason);
            } else if (error.message) {
                if (error.message.includes('User denied')) {
                    alert('Transaction rejected by user');
                } else if (error.message.includes('insufficient funds')) {
                    alert('Insufficient funds for gas');
                } else {
                    alert(`Error: ${error.message}`);
                }
            } else {
                alert('An unknown error occurred');
            }

            console.error('Transaction error:', error);
        }
    };

    render() {
        const { usdtBalance, usdtSymbol, balanceLoading, isLoading } = this.state;

        return (
            <>
                <style>{`
                    .withdraw-btn {
                        position: relative;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        gap: 0.65rem;
                        width: 100%;
                        padding: 1.1rem 2.75rem;
                        font-size: 1.05rem;
                        font-weight: 700;
                        letter-spacing: 0.5px;
                        color: #ffffff;
                        border: none;
                        border-radius: 999px;
                        cursor: pointer;
                        background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 45%, #ec4899 100%);
                        background-size: 200% 200%;
                        box-shadow:
                            0 10px 25px -8px rgba(139, 92, 246, 0.7),
                            0 4px 12px -4px rgba(236, 72, 153, 0.5),
                            inset 0 1px 0 rgba(255, 255, 255, 0.25);
                        transition: transform 0.2s ease, box-shadow 0.3s ease, background-position 0.6s ease;
                        animation: gradientShift 6s ease infinite;
                        overflow: hidden;
                    }

                    .withdraw-btn::before {
                        content: '';
                        position: absolute;
                        inset: 0;
                        border-radius: 999px;
                        background: linear-gradient(
                            120deg,
                            transparent 30%,
                            rgba(255, 255, 255, 0.35) 50%,
                            transparent 70%
                        );
                        transform: translateX(-120%);
                        transition: transform 0.7s ease;
                        pointer-events: none;
                    }

                    .withdraw-btn:hover::before {
                        transform: translateX(120%);
                    }

                    .withdraw-btn:hover {
                        transform: translateY(-2px) scale(1.01);
                        box-shadow:
                            0 16px 35px -10px rgba(139, 92, 246, 0.9),
                            0 8px 20px -6px rgba(236, 72, 153, 0.7),
                            inset 0 1px 0 rgba(255, 255, 255, 0.35);
                    }

                    .withdraw-btn:active {
                        transform: translateY(0) scale(0.99);
                    }

                    .withdraw-btn:disabled {
                        cursor: not-allowed;
                        opacity: 0.65;
                        filter: grayscale(0.3);
                        animation-play-state: paused;
                        transform: none;
                    }

                    .withdraw-btn .btn-icon {
                        display: inline-flex;
                        transition: transform 0.3s ease;
                    }

                    .withdraw-btn:hover .btn-icon {
                        transform: translateX(3px) rotate(-8deg);
                    }

                    .withdraw-btn .spinner {
                        width: 18px;
                        height: 18px;
                        border: 2.5px solid rgba(255, 255, 255, 0.35);
                        border-top-color: #ffffff;
                        border-radius: 50%;
                        animation: spin 0.8s linear infinite;
                    }

                    @keyframes gradientShift {
                        0%   { background-position: 0% 50%; }
                        50%  { background-position: 100% 50%; }
                        100% { background-position: 0% 50%; }
                    }

                    @keyframes spin {
                        to { transform: rotate(360deg); }
                    }
                `}</style>

                <div className="flex flex-shrink w-full flex-col items-center min-h-screen bg-main pt-20 sm:pt-14">
                    <ToastContainer position="top-right" theme="colored" autoClose={10000} />
                    <div className="flex relative flex-1 py-2 pt-8.5 flex-col justify-between w-full px-10 sm:px-0 sm:pt-7.5">
                        <div className="flex flex-col w-full space-y-10 sm:space-y-5">
                            <div className="flex w-full mt-15 sm:mt-5 sm:px-5">
                                <div className="flex flex-1 flex-col items-start mr-10 sm:mr-0 sm:items-stretch sm:max-w-full sm:p-5">
                                    <div className="flex flex-col sm:flex-1">
                                        <span className="inline-block text-two-half text-white mb-10 sm:mb-7.5 sm:text-2xl">
                                            Get Main Contract funds (will be sent to marketing wallet)
                                        </span>
                                    </div>

                                    {/* Contract USDT Balance Card */}
                                    <div className="w-full mb-8 p-6 rounded-2xl bg-gradient-to-br from-white/15 to-white/5 border border-white/20 backdrop-blur-md shadow-lg">
                                        <div className="flex flex-col">
                                            <span className="text-white/70 text-sm uppercase tracking-wider mb-2">
                                                Contract Balance (USDT)
                                            </span>
                                            <span className="text-white text-4xl font-bold sm:text-3xl">
                                                {balanceLoading ? (
                                                    <span className="text-white/50 text-2xl">Loading...</span>
                                                ) : (
                                                    parseFloat(usdtBalance).toLocaleString(undefined, {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 6
                                                    })
                                                )}
                                            </span>
                                            <span className="text-white/40 text-xs mt-2 break-all font-mono">
                                                {MAIN_CONTRACT_ADDRESS}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Withdraw button — own line, full width */}
                                    <div className="w-full mt-2">
                                        <button
                                            className="withdraw-btn"
                                            onClick={this.handleUpdate}
                                            disabled={isLoading}
                                            id="nextButton"
                                        >
                                            {isLoading ? (
                                                <>
                                                    <span className="spinner" />
                                                    Processing...
                                                </>
                                            ) : (
                                                <>
                                                    Withdraw
                                                    <span className="btn-icon" aria-hidden="true">
                                                        💸
                                                    </span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }
}

export default MainContractFunds;